/**
 * F 381 — THE LANE GRAPH. The shared vocabulary between the FCC page and the
 * VPS bridge.
 *
 * WHY THERE IS NO `chat` KIND
 * --------------------------
 * The dispatch asked for "`chat` nodes". The FCC taxonomy has no `chat` kind
 * — and it already has a chat data model, which is better:
 *
 *   "FVC 172-spine added turn/agent/span to support VC chat migration.
 *    **turn is the atomic conversational unit; agent is the actor that owns
 *    a conversation**; span is telemetry inside a turn."
 *                                            — src/core/adapters/filesystem/types.ts
 *
 * `Turn.input` is "User message or triggering action" and `Turn.output` is
 * "VC's response". So ONE turn IS one exchange, and "the lane agent" IS an
 * `agent` node. Inventing a `chat` kind beside `turn` would put two ways of
 * saying "a message" into one canonical taxonomy, which FVC 170 forbids
 * ("ON TOP OF ... not as a forked list"). This module therefore uses the
 * existing kinds.
 *
 * THE KEY CONVENTION
 * ------------------
 * The FCC graph is a flat KV served by `fcc-api`, and `syncable()` accepts
 * only keys that start with `freshcards:`. The QueryLayer's own convention is
 * `<kind>:<id>`, so a turn lives at `freshcards:turn:<id>` on disk and on the
 * server.
 *
 * WRITES GO THROUGH `RemoteState.remoteAdapter`, NOT `LocalStorageAdapter`.
 * `LocalStorageAdapter.save()` writes localStorage and nothing else — it does
 * not sync. `remoteAdapter.save()` goes through `RemoteState.set()`, which
 * writes localStorage, updates the cache, and queues the server flush. A turn
 * written through the wrong one never reaches the bridge, and the bridge is on
 * the other side of the server.
 *
 * THE THREE STATES OF A TURN
 * --------------------------
 *   PENDING   initiatedBy: 'user', output: ''   → the bridge picks it up
 *   ANSWERED  initiatedBy: 'user', output: '…'  → the reply is in it
 *
 * The bridge answers by UPDATING THE SAME NODE, because `Turn.output` is
 * defined as the response. One node per exchange, no pairing heuristic, and
 * no second node for the reply to be lost.
 */

import { RemoteState } from '../storage/RemoteState';
import { createAgent, type Agent } from '../../types/models/Agent';
import { createConversation, type Conversation } from '../../types/models/Conversation';
import { createProject, type Project } from '../../types/models/Project';
import { createTurn, type Turn } from '../../types/models/Turn';
import { graphFetch } from '../storage/graphAuth';

/** The key prefix the FCC KV accepts. `syncable()` rejects anything else. */
export const FCC_KEY_PREFIX = 'freshcards:';

/** Deterministic ids. The page and the bridge must agree without a handshake. */
export const LANE_PROJECT_ID = 'proj-lane';
export const LANE_AGENT_ID = 'lane-agent';
export const LANE_CONVERSATION_ID = 'lane-conversation';
export const LANE_AGENT_LABEL = 'Lane';

/** The storage key for one node. */
export const laneKey = (kind: 'project' | 'agent' | 'conversation' | 'turn', id: string): string =>
  `${FCC_KEY_PREFIX}${kind}:${id}`;

/** The KV prefix a kind's nodes live under, on the server. */
export const lanePrefix = (kind: 'project' | 'agent' | 'conversation' | 'turn'): string =>
  `${FCC_KEY_PREFIX}${kind}:`;

/**
 * The server-side poll.
 *
 * `RemoteState` hydrates ONCE at boot and never polls, so a reply written by
 * the bridge would sit on the server unseen until a reload. The Lane Chat
 * polls this route directly. It is the same route `RemoteState` itself uses
 * (`/api/fcc` + `/kv`), so there is no second data path.
 *
 * DEGRADES, never throws: on a static deploy with no `/api/fcc`, the page
 * still works from the local cache.
 */
export async function fetchRemoteValues(
  prefix: string,
): Promise<Record<string, string> | null> {
  try {
    // F 390 — the graph is AUTHENTICATED now, so this fetch carries the
    // token. It goes through the ONE graph fetch, so there is exactly one
    // place that knows the graph needs a credential.
    const res = await graphFetch('/api/fcc', `/kv?prefix=${encodeURIComponent(prefix)}`);
    if (!res || !res.ok) return null;
    const body = (await res.json()) as { ok?: boolean; values?: Record<string, string> };
    if (!body || body.ok !== true || !body.values) return null;
    return body.values;
  } catch {
    return null;
  }
}

/** Every node of one kind, from the server if it is reachable, else the cache. */
export async function listLaneNodes<T>(kind: 'project' | 'agent' | 'conversation' | 'turn'): Promise<T[]> {
  const remote = await fetchRemoteValues(lanePrefix(kind));
  const out = new Map<string, T>();

  // The server first, so a bridge-written node wins over a stale cache entry.
  if (remote) {
    for (const [key, raw] of Object.entries(remote)) {
      if (!key.startsWith(lanePrefix(kind))) continue;
      try {
        out.set(key, JSON.parse(raw) as T);
      } catch {
        /* a malformed node is not a node; skip it rather than crash the page */
      }
    }
  }
  // Then the local cache, for anything the server has not seen yet.
  const local = await RemoteState.remoteAdapter.list(lanePrefix(kind));
  for (const key of local) {
    if (out.has(key)) continue;
    const value = await RemoteState.remoteAdapter.load(key);
    if (value) out.set(key, value as T);
  }
  return [...out.values()];
}

/** Write one node, through the adapter that actually syncs. */
export async function saveLaneNode<T>(kind: 'project' | 'agent' | 'conversation' | 'turn', id: string, value: T): Promise<void> {
  await RemoteState.remoteAdapter.save(laneKey(kind, id), value);
  // The adapter queues a 400ms debounced flush. A chat message that waits
  // 400ms to leave the browser is fine, but a flush that FAILS silently is
  // not — so flush explicitly and report whether it landed.
  await RemoteState.flush(true);
}

/** The lane project. Created once; its id is deterministic. */
export async function ensureLaneProject(): Promise<Project> {
  const existing = (await listLaneNodes<Project>('project')).find((p) => p.id === LANE_PROJECT_ID);
  if (existing) return existing;
  const project = createProject({ id: LANE_PROJECT_ID, name: 'Lane' });
  await saveLaneNode('project', project.id, project);
  return project;
}

/** The lane agent — the actor that owns the conversation. */
export async function ensureLaneAgent(project: Project): Promise<Agent> {
  const existing = (await listLaneNodes<Agent>('agent')).find((a) => a.id === LANE_AGENT_ID);
  if (existing) return existing;
  const agent = createAgent({
    id: LANE_AGENT_ID,
    label: LANE_AGENT_LABEL,
    project: project.id,
    kind: 'other',
    liveness: 'live',
    interactive: true,
  });
  await saveLaneNode('agent', agent.id, agent);
  return agent;
}

/** The lane conversation. */
export async function ensureLaneConversation(project: Project, agentId: string): Promise<Conversation> {
  const existing = (await listLaneNodes<Conversation>('conversation')).find(
    (c) => c.id === LANE_CONVERSATION_ID,
  );
  if (existing) return existing;
  const conversation = createConversation({
    id: LANE_CONVERSATION_ID,
    title: 'Lane Chat',
    project: project.id,
    participants: [agentId],
  });
  await saveLaneNode('conversation', conversation.id, conversation);
  return conversation;
}

/** The turns belonging to the lane agent, oldest first. */
export async function listLaneTurns(agentId: string): Promise<Turn[]> {
  const turns = await listLaneNodes<Turn>('turn');
  return turns
    .filter((t) => t && t.agentId === agentId)
    .sort((a, b) => {
      const sa = typeof a.sequence === 'number' ? a.sequence : 0;
      const sb = typeof b.sequence === 'number' ? b.sequence : 0;
      if (sa !== sb) return sa - sb;
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    });
}

/** A turn the bridge still owes an answer to. */
export function isPendingTurn(turn: Turn): boolean {
  return turn.initiatedBy === 'user' && (turn.output ?? '') === '';
}

/** A new user turn for the lane. */
export function makeUserTurn(input: {
  text: string;
  project: string;
  conversationId: string;
  agentId: string;
  sequence: number;
}): Turn {
  return createTurn({
    input: input.text,
    project: input.project,
    conversationId: input.conversationId,
    agentId: input.agentId,
    sequence: input.sequence,
    initiatedBy: 'user',
    output: '',
    status: 'complete',
  });
}
